import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Layout } from "../components/Layout";
import { api } from "../api/client";
import { INCIDENT_SEVERITIES, INCIDENT_TYPES, incidentTypeLabel, severityLabel } from "../lib/incidents";

export function NewIncidentPage() {
  const navigate = useNavigate();

  const [buildingId, setBuildingId] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [unitId, setUnitId] = useState("");
  const [incidentType, setIncidentType] = useState("");
  const [incidentDatetime, setIncidentDatetime] = useState("");
  const [severity, setSeverity] = useState("medium");
  const [reporterPhone, setReporterPhone] = useState("");
  const [description, setDescription] = useState("");
  const [followUpAction, setFollowUpAction] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const buildingsQ = useQuery({
    queryKey: ["ref", "buildings"],
    queryFn: () => api.reference.buildings(),
  });
  const tenantsQ = useQuery({
    queryKey: ["ref", "tenants", buildingId],
    queryFn: () => api.reference.tenants(Number(buildingId)),
    enabled: Boolean(buildingId),
  });
  const unitsQ = useQuery({
    queryKey: ["ref", "units", buildingId],
    queryFn: () => api.reference.units(Number(buildingId)),
    enabled: Boolean(buildingId),
  });

  const createMut = useMutation({
    mutationFn: () =>
      api.incidents.create({
        buildingId: Number(buildingId),
        tenantId: tenantId ? Number(tenantId) : null,
        unitId: unitId ? Number(unitId) : null,
        incidentType: incidentType || null,
        incidentDatetime,
        severity,
        description: description.trim(),
        reporterPhone: reporterPhone.trim() || null,
        followUpAction: followUpAction.trim() || null,
        notes: notes.trim() || null,
      }),
    onSuccess: ({ id }) => navigate(`/incidents/${id}`),
    onError: () => setError("Could not save the incident. Please try again."),
  });

  function onSave() {
    setError(null);
    if (!buildingId) return setError("Please choose a building.");
    if (!incidentDatetime) return setError("Please enter when the incident happened.");
    if (!description.trim()) return setError("Please describe what happened.");
    createMut.mutate();
  }

  return (
    <Layout title="Add an Incident">
      <p className="help-line">
        Choose the building, then record what happened. The tenant and unit are optional.
      </p>
      <section className="card form-card">
        <label className="field">
          <span>Building</span>
          <select
            value={buildingId}
            onChange={(e) => {
              setBuildingId(e.target.value);
              setTenantId("");
              setUnitId("");
            }}
          >
            <option value="">Choose a building…</option>
            {(buildingsQ.data?.buildings ?? []).map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </label>

        <div className="two-col">
          <label className="field">
            <span>Tenant (optional)</span>
            <select
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              disabled={!buildingId}
            >
              <option value="">No specific tenant</option>
              {(tenantsQ.data?.tenants ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Unit / location (optional)</span>
            <select
              value={unitId}
              onChange={(e) => setUnitId(e.target.value)}
              disabled={!buildingId}
            >
              <option value="">No specific unit</option>
              {(unitsQ.data?.units ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  Unit {u.unitNumber}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="two-col">
          <label className="field">
            <span>Type</span>
            <select value={incidentType} onChange={(e) => setIncidentType(e.target.value)}>
              <option value="">Not set</option>
              {INCIDENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {incidentTypeLabel(t)}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Severity</span>
            <select value={severity} onChange={(e) => setSeverity(e.target.value)}>
              {INCIDENT_SEVERITIES.map((s) => (
                <option key={s} value={s}>
                  {severityLabel(s)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="field">
          <span>Date &amp; time of the incident</span>
          <input
            type="datetime-local"
            value={incidentDatetime}
            onChange={(e) => setIncidentDatetime(e.target.value)}
          />
        </label>

        <label className="field">
          <span>Phone number</span>
          <input value={reporterPhone} onChange={(e) => setReporterPhone(e.target.value)} />
        </label>

        <label className="field">
          <span>What happened?</span>
          <textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>

        <label className="field">
          <span>Follow-up action</span>
          <textarea
            rows={3}
            value={followUpAction}
            onChange={(e) => setFollowUpAction(e.target.value)}
          />
        </label>

        <label className="field">
          <span>Notes</span>
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>

        {error && <p className="field-error">{error}</p>}

        <div className="form-actions">
          <button
            className="btn btn-ghost btn-xl"
            onClick={() => navigate("/incidents")}
            disabled={createMut.isPending}
          >
            Cancel
          </button>
          <button className="btn btn-primary btn-xl" onClick={onSave} disabled={createMut.isPending}>
            {createMut.isPending ? "Saving…" : "Save incident"}
          </button>
        </div>
      </section>
    </Layout>
  );
}
